import { AxiosRequestConfig } from 'axios';

export enum ConsentCookies {
  analytics = 'analytics',
  functional = 'functional',
  required = 'required'
}

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

type Either<A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, R, S, T, U, V, W, X, Y, Z, AA, AB, AC, AD, AE, AF, AG, AH, AI, AJ> =
  A
  | B
  | C
  | D
  | E
  | F
  | G
  | H
  | I
  | J
  | K
  | L
  | M
  | N
  | O
  | P
  | R
  | S
  | T
  | U
  | V
  | W
  | X
  | Y
  | Z
  | AA
  | AB
  | AC
  | AD
  | AE
  | AF
  | AG
  | AH
  | AI
  | AJ;

export interface EmptyObject {
  [key: string]: any;
}

export interface ActionsObj {
  [key: string]: Action;
}

export enum SearchRecordBy {
  index = 'index',
  uniqueField = 'uniqueField',
  selected = 'selected'
}

export interface LoopObjectType {
  breakOperations?: Operation[]
  counter?: number
  increment?: number
  condition: string,
  variables?: {
    [key: string]: any
  }
}

export interface ActionType {
  buffer?: number;
  canceledStackActions?: string[] | null;
  delay?: number;
  dispatcherId?: string;
  executeOnlyOperationType?: string;
  loop?: LoopObjectType | null;
  persistent?: string[];
  pos?: Position;
  propagationIndependent?: boolean | null;
  stack?: boolean;
  template?: any;
}

export enum Position {
  start = 'start',
  end = 'end',
  endCorner = 'endCorner',
  conditionallyExecuted = 'conditionallyExecuted'
}

export enum MaskType {
  LIGHT_SQUARE= 'LIGHT_SQUARE',
  LIGHT_FILL= 'LIGHT_FILL',
  SPLASH= 'SPLASH'
}

export enum RestApiPrefix {
  none = '',
  default = 'default', // TODO remove RestApiPrefix enum after all endpoints prefixes are standardised
  webApi = 'webApi/v0'
}

export enum OperationType {
  action = 'action',
  addOperationData = 'addOperationData',
  addPersistentData = 'addPersistentData',
  cancelRequest = 'cancelRequest',
  cleanUpAction = 'cleanUpAction',
  closeWindow = 'closeWindow',
  cookie = 'cookie',
  conditional = 'conditional',
  data = 'data',
  deleteAllOperationData = 'deleteAllOperationData',
  dialog = 'dialog',
  generateOperation = 'generateOperation',
  language = 'language',
  maskModule = 'maskModule',
  mergeAsyncRequest = 'mergeAsyncRequeredirectst',
  pingDownload = 'pingDownload',
  popupWindow = 'popupWindow',
  redirect = 'redirect',
  removePersistentData = 'removePersistentData',
  request = 'request',
  signIn = 'signIn',
  signOut = 'signOut',
  stack = 'stack',
  stopAll = 'stopAll',
  switch = 'switch',
  syncDispatchersDataItem = 'syncDispatchersDataItem',
  theme = 'theme',
  unmaskModule = 'unmaskModule',
  unmaskAllModule = 'unmaskAllModule',
  unlink = 'unlinkDispatcher',
  warning = 'warning',
  widget = 'widget',
  updateDialog = 'updateDialog',
  upload = 'upload',
  webSocket = 'webSocket',
  webSocketFakePing = 'webSocketFakePing',
  windowManager = 'windowManager',
  promise = 'promise',
  isModuleMounted = 'isModuleMounted'
}

// <debug>
// used internally for Dispatcher Editor - do not expose this parameter
export interface DispatcherEditorProperties {
  evaluated?: {
    [key: string]: string
  },
  internalOperation?: boolean,
  complexParentOperationId?: string
}
// </debug>

export interface OptionalOperationProperties extends DispatcherEditorProperties {
  actionName?: string;
  actionType?: ActionType;
  aggregatedResult?: boolean;
  args?: string[];
  continueAnyway?: boolean,
  data?: any;
  debug?: boolean | string;
  delay?: number,
  dependence?: string | string[]; // dependence can be array of strings but just internally, do not expose to DE
  dependent?: boolean; // Used on pingOperations with OperationType.upload to indicate that those action will be
  // dependant on ping request ajax
  dispatcherId?: string,
  executeType?: string,
  evaluate?: boolean,
  handleError?: boolean,
  independent?: boolean,
  index?: number,
  isPromise?: boolean | string,
  log?: boolean | string,
  mappingName?: string,
  moduleId?: string,
  moduleName?: string,
  name?: string,
  persistent?: boolean | string[],
  propertiesToEvaluate?: string[],
  specialData?: any,
  stopExecOperations?: boolean,
  subName?: string,
  synchronous?: boolean,
  waitForModule?: boolean,
  displayed?: boolean
}

export interface AddOperationDataOperation extends OptionalOperationProperties {
  aggregateResult?: boolean;
  isPromiseAggregateExecute?: boolean;
  isCallback?: boolean;
  moduleName: string;
  name: string;
  persistent?: boolean;
  type: OperationType.addOperationData;
}

export interface BaseOperation extends OptionalOperationProperties {
  args?: string[];
  actionType?: ActionType;
  data?: any;
  name?: string;
  disabled?: any;
  hidden?: any;
  type?: undefined
}

export interface ConditionOperation extends OptionalOperationProperties {
  condition: string;
  conditionOperations: Action;
  elseOperations?: Action;
  type: OperationType.conditional;
}

export enum CookieMethod {
  checkConsent = 'checkConsent',
  consentUrl = 'consentUrl',
  defaultDuration = 'defaultDuration',
  getCookie = 'getCookie',
  setCookie = 'setCookie',
  removeCookie = 'removeCookie'
}

export interface CookieOperation extends OptionalOperationProperties {
  type: OperationType.cookie;
  method: CookieMethod.getCookie | CookieMethod.setCookie | CookieMethod.removeCookie | CookieMethod.consentUrl | CookieMethod.checkConsent | CookieMethod.defaultDuration;
  cookieType?: ConsentCookies.analytics | ConsentCookies.functional | ConsentCookies.required;
}

export enum WindowType {
  any = 'any',
  dialog = 'dialog',
  warning = 'warning'
}

export interface CloseOperation extends Omit<OptionalOperationProperties, 'index'> {
  index?: DialogPosition.last | DialogPosition.all | DialogPosition.previous | number; // default DialogPosition.last
  windowType?: WindowType.any | WindowType.dialog | WindowType.warning; // default WindowType.any
  type: OperationType.closeWindow;
}

export enum ThemeLoad {
  change = 'change',
  init = 'init'
}

export interface ThemeOperation extends OptionalOperationProperties {
  load: ThemeLoad.change | ThemeLoad.init;
  type: OperationType.theme;
}

export interface LanguageOperation extends OptionalOperationProperties {
  change?: boolean; // default false // invokes changeLanguage
  type: OperationType.language;
}

export enum SignType {
  init = 'init',
  request = 'request'
}

export interface SignOperation extends OptionalOperationProperties {
  subType: SignType.init | SignType.request;
  type: OperationType.signIn | OperationType.signOut;
  // signIn && SignType.init      -> loginRequest
  // signIn && SignType.request   -> signInRequest
  // signOut && SignType.init     -> signOutWarning
  // signOut && SignType.request  -> signOutRequest
}

export interface ActionOperation extends OptionalOperationProperties {
  data?: any;
  actionType?: ActionType;
  name: string;
  internalOperation?: boolean;
  type: OperationType.action;
}

export interface StackOperation extends OptionalOperationProperties {
  actionType: ActionType;
  data?: any;
  name: string;
  startTime?: number;
  type: OperationType.stack;
}

export interface SwitchOperation extends Record<string, any>, OptionalOperationProperties {
  condition: string;
  defaultOperations?: Action;
  type: OperationType.switch;
}

export interface RequestDataBase {
  subName?: string;
  mergeRequest?: string; // internal property, do not expose to DE
  partOfMergeRequest?: string; // internal property, do not expose to DE
  usePrefix?: RestApiPrefix;
  concurrently?: boolean;
  dispatcherId?: string;
  isDownload?: boolean;
  downloadType?: 'form' | 'blob';
  isAuthRest?: boolean;
  isRest?: boolean;
  isUpload?: boolean;
  method?: string;
  headers?: object;
  handleError?: boolean;
  uploadGroupId?: string;
  url: string;
  urlParams?: any
  withoutResponse?: boolean;
  params?: any;
  /**
   * Params that applies only on mounting
   */
  paramsOnMounting?: any;
}

export interface UploadFileFormData {
  [key: string]: FormData;
}

export interface UploadData {
  actionName: string;
  cancelRequest: Function;
  dispatcherId: string;
  fileName: string;
  uploadGroupId?: string;
}

export interface MultipleUploadData {
  actionName: string;
  additionalConfig: AxiosRequestConfig;
  dispatcherId: string;
  method: string;
  multipleParams: UploadFileFormData;
  uploadGroupId?: string;
  url: string;
}

export interface RequestData extends Optional<RequestDataBase, 'dispatcherId' | 'url'> {
  template?: Optional<RequestDataBase, 'dispatcherId' | 'url'> & {
    bind?: any;
  };
}

export enum ErrorCodes {
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  METHOD_NOT_ALLOWED = 405,
  PRECONDITION_REQUIRED = 428,
  PAYLOAD_TOO_LARGE = 413,
  UNSUPPORTED_MEDIA_FILE = 415,
  INTERNAL_SERVER_ERROR = 500,
  NOT_IMPLEMENTED = 501,
  BAD_GATEWAY = 502,
  NETWORK_AUTHENTICATION_REQUIRED = 511,
  LOGIN_TIME_OUT = 440,
  REQUEST_TIMEOUT = 408,
  CONFLICT = 409
}

export interface RequestOperation extends OptionalOperationProperties {
  mergeRequest?: string; // internal property, do not expose to DE
  partOfMergeRequest?: string; // internal property, do not expose to DE
  data: RequestData;
  handleErrorStatuses?: ErrorCodes[];
  persistent?: boolean;
  synchronous?: boolean;
  type: OperationType.request;
}

export interface MergeAsyncRequest extends Omit<RequestOperation, 'type'> {
  unique: string;
  operations: Operation[];
  type: OperationType.mergeAsyncRequest;
}

export interface PingDownloadOperation extends OptionalOperationProperties {
  afterDownloadOperations?: Operation[]
  ping: { template: any } | RequestData;
  pingBreakCondition: string[];
  pingCondition: string;
  delay: number;
  pingOperations?: Action;
  pingErrorOperations: Operation[];
  download: { template: any } | RequestData;
  beforeDownloadOperations: Operation[];
  mappingName: string;
  withIndicator?: boolean;
  type: OperationType.pingDownload;
  complexOperationId?: string; // used only for dispatcher editor, do not expose this property
  handleErrorStatuses?: ErrorCodes[];
}

export interface UploadOperation extends OptionalOperationProperties {
  mappingName?: string; // server response object
  uniqueParameter?: string; // map to server response
  uploadCondition: string; // condition to check on resolving initialRequest
  showProgress?: boolean; // if true upload widget will be shown with progress, default value is false
  initialData?: RequestData;
  afterInitialRequest?: Action;
  afterInitialRequestHandleErrorStatus?: Action,
  afterPingRequestHandleErrorStatus?: Action,
  data: RequestData;
  operations?: Action;
  progressOperations?: Action;
  responseFiles?: {
    rejected: string,
    uploaded: string
  };
  handleErrorStatuses?: ErrorCodes[] | string;
  handleFileRejection?: Action;
  type: OperationType.upload;
}

export interface PersistentData extends OptionalOperationProperties {
  persistent: string[];
  type: OperationType.addPersistentData;
}

export interface PersistentOperation extends OptionalOperationProperties {
  persistent: string[];
}

export interface AddPersistentDataOperation extends PersistentOperation {
  type: OperationType.addPersistentData;
}

export interface RemovePersistentDataOperation extends PersistentOperation {
  type: OperationType.removePersistentData;
}

export interface MaskOperation extends OptionalOperationProperties {
  localeProperty?: string;
  moduleName: string;
  className?: string;
  maskType?: string;
  type: OperationType.maskModule;
}

export interface UnmaskOperation extends OptionalOperationProperties {
  localeProperty?: string;
  moduleName: string;
  type: OperationType.unmaskModule;
}

export interface UnmaskAllModuleOperation extends OptionalOperationProperties {
  type: OperationType.unmaskAllModule;
}

export interface GenerateOperation extends OptionalOperationProperties {
  args?: string[];
  data?: any;
  mappingName: string;
  moduleName: string;
  type: OperationType.generateOperation;
}

export interface DeleteAllOperationDataOperation extends OptionalOperationProperties {
  args?: string[];
  data?: any;
  name: string;
  moduleName: string;
  type: OperationType.deleteAllOperationData;
}

export interface SyncDispatcherItemsOperation extends OptionalOperationProperties {
  items: string[]; // list of items to sync across dispatchers
  referenceDispatcherId?: string; // dispatcher which data will be passed to other dispatchers
  dispatcherIds?: string[]; // into which dispatcher data will be passed, by default will be passed to all dispatchers
  actions?: string[];
  type: OperationType.syncDispatchersDataItem;
}

export enum Stop {
  all = 'all',
  operations = 'operations',
  stacks = 'stacks'
}

export interface StopAllOperation extends OptionalOperationProperties {
  data?: boolean; // If true, then appOperationData will be cleared for every dispatcher
  dispatcherIds?: string[]; // By default only current Dispatcher will be cleared
  isAnotherDispatchers?: boolean;
  items?: Stop[];
  partialMatch?: boolean; // if partialMatch is true, then all dispatchers with partial match will be cleared
  type: OperationType.stopAll;
}

export interface UnlinkOperation extends OptionalOperationProperties {
  dispatcherIds?: string[]; // By default only current Dispatcher will be unlinked
  partialMatch?: boolean; // if partialMatch is true, then all dispatchers with partial match will be unlinked
  type: OperationType.unlink;
}

export interface AccessibilityButtonConfig {
  ariaLabel?: string
  iconAriaHidden?: boolean
}

export interface ButtonConfig {
  rounded?: string | null,
  shape?: string | null,
  size?: string | null,
  accessibility?: AccessibilityButtonConfig | boolean,
  template?: any;
  className?: string;
  dock?: DockType;
  hidden?: boolean | string;
  fillMode?: string;
  pos?: Position;
  themeColor?: string | null;
  iconClass?: string;
  customTagType?: CustomTagType;
  args?: string[];
  disabled?: boolean;
}

export interface ButtonActionConfig {
  close?: boolean;
  placeholders?: any;
  operations?: Action;
  btnName: string;
  btnConfig?: ButtonConfig;
  runningOperationsAfterClose?: boolean
}

export enum CustomTagType {
  counter = 'counter',
  buttonCounter = 'buttonCounter',
  label = 'label',
  notificationLabel = 'notificationLabel',
  notificationLabelWithCounter = 'notificationLabelWithCounter',
  buttonGroup = 'buttonGroup',
  switcher = 'switcher',
  closeIcon = 'closeIcon'
}

export enum DockType {
  top = 'top',
  bottom = 'bottom'
}

export enum WarningType {
  info = 'info',
  warning = 'warning',
  alert = 'alert',
  error = 'error',
  success = 'success'
}

export interface WarningOperation extends OptionalOperationProperties {
  accessibility?: AccessibilityWidget,
  closable?: boolean;
  className?: string;
  closeAllPreviousWarning?: boolean;
  data?: any;
  dialogName?: string,
  items?: ButtonActionConfig[];
  placeholders?: any;
  previousFocus?: boolean | string;
  text?: string;
  title: string;
  subTitle?: string;
  type: OperationType.warning;
  warningType?: WarningType;
  skipTranslation?: boolean;
  resizable?: boolean;
}

export interface DialogButton {
  actionName: string,
  btnConfig?: ButtonConfig,
  close?: boolean,
  name: string,
  placeholders?: { [key: string]: string }
}

export interface ChildrenType {
  render: Function | any;
  propArgs?: string[];
  props?: { [key: string]: any };
  renderType?: RenderChildrenType;
}

export enum RenderChildrenType {
  first = 'ADD_AS_FIRST',
  last = 'ADD_AS_LAST',
  override = 'OVERRIDE_CONTENT'
}

export enum DialogPosition {
  all = 'all',
  allPrevious = 'all previous',
  last = 'last',
  previous = 'previous'
}

export interface DialogConfig {
  accessibility?: AccessibilityWidget,
  buttons: DialogButton[] | Operation[];
  className?: string;
  closable?: boolean;
  dispatcherId: string;
  placeholders?: { [key: string]: string };
  previousFocus?: boolean | string;
  pos?: number | DialogPosition.last | DialogPosition.previous;
  icon?: string | null;
  text: string;
  title: string;
  subTitle?: string;
  warningType?: WarningType;
  skipTranslation?: boolean; // skipTranslation is true only when title and text properties are already sent
  // localised from server
  resizable?: boolean;
}

export interface DialogOperation extends OptionalOperationProperties {
  accessibility?: AccessibilityWidget,
  children?: ChildrenType[];
  className?: string;
  closable?: boolean;
  closeAllPreviousWarning?: boolean;
  data?: any;
  dialogName?: string;
  items?: ButtonActionConfig[];
  placeholders?: any;
  previousFocus?: boolean | string;
  text?: string;
  title?: string;
  subTitle?: string;
  warningType?: WarningType;
  type: OperationType.dialog;
  skipTranslation?: boolean;
  resizable?: boolean;
  inserted?: boolean
}

export enum UpdateDialogScope {
  active = 'ACTIVE',
  all = 'ALL'
}

export interface UpdateDialog extends OptionalOperationProperties {
  children?: ChildrenType[];
  data?: any;
  dialogName?: string,
  customTags?: CustomTags[];
  buttons?: UpdateButton[];
  scope?: UpdateDialogScope,
  type: OperationType.updateDialog;
}

export interface UpdateButton {
  hidden?: boolean | string;
  name: string;
  disabled?: boolean | string;
}

export interface UpdateStateModule {
  mappingName?: string,
  moduleId?: string,
  executeType?: string
}

export interface UpdateStateDispatcherModule extends UpdateStateModule {
  moduleName: string
}

export interface CustomTags {
  name: string;
  args?: string[];
  disabled?: string | boolean,
  hidden?: string | boolean;
}

export interface CustomTagProps {
  name?: string;
  args: (string | number)[];
  type: CustomTagType;
  key: string;
}

export enum WidgetStage {
  minimized = 'MINIMIZED',
  fullScreen = 'FULLSCREEN',
  default = 'DEFAULT'
}

export interface ConnectShowData {
  children: ChildrenType[];
  render: string | any;
  props: {
    className?: string;
    id: string;
    title: string;
    subTitle?: string;
    warningType?: WarningType;
    items?: ButtonActionConfig[];
    buttons: DialogButton[];
    placeholders: any;
    previousFocus?: boolean | string;
    dispatcherId: string;
    resizable: boolean;
    draggable: boolean;
    width: number;
    height: number;
    minWidth: number;
    minHeight: number;
  };
}

export enum AccessibilityWidgetAriaLive {
  assertive = 'assertive',
  off = 'off',
  polite = 'polite'
}

export enum AccessibilityWidgetRole {
  alert = 'alert',
  log = 'log',
  status = 'status'
}

export interface AccessibilityWidget {
  ariaLive: AccessibilityWidgetAriaLive,
  role: AccessibilityWidgetRole
}

export interface WidgetOperation extends OptionalOperationProperties {
  accessibility?: AccessibilityWidget,
  className?: string;
  children?: ChildrenType[];
  draggable?: true;
  items?: ButtonActionConfig[];
  minWidth?: number;
  minHeight?: number;
  name?: string;
  height?: number;
  placeholders?: any;
  previousFocus?: boolean | string;
  resizable?: boolean;
  title: string;
  subTitle?: string;
  warningType?: WarningType;
  type: OperationType.widget;
  width?: number;
}

export interface PopupWindowOperation extends OptionalOperationProperties {
  pos?: 'previous-screen' | 'center-screen' | 'center-parent';
  children?: ChildrenType[];
  className?: string;
  closable?: boolean;
  data?: any;
  items?: ButtonActionConfig[];
  onCloseOperations?: Operation[];
  placeholders?: any;
  height?: number;
  width?: number;
  text?: string;
  title: string;
  windowId?: string;
  type: OperationType.popupWindow;
}

export interface RedirectOperation extends OptionalOperationProperties {
  consent?: boolean; // invokes redirectToConsentPage, default is false
  data?: any;
  signIn?: boolean; // invokes redirectToSignIn, default is false
  type: OperationType.redirect;
}

export interface PromiseConfig {
  arg: any;
  operation: PromiseOperation;
  module: any;
  operationItem: any;
  lastExecutedModule?: boolean
}

export interface PromiseOperation extends OptionalOperationProperties {
  args?: string[];
  isCallback: boolean;
  isPromise: boolean | string;
  handleError?: boolean;
  moduleName: string;
  name: string;
  type: OperationType.promise
}

export interface WebSocketOperation extends OptionalOperationProperties {
  connected?: boolean;
  socketId?: string;
  operations?: Operation[];
  requestConfig?: RequestData,
  type: OperationType.webSocket;
}

export interface WebSocketFakePingOperation extends OptionalOperationProperties {
  delay?: number;
  socketId: string;
  data: any;
  operations?: Operation[];
  type: OperationType.webSocketFakePing;
}

export enum WindowManagerOperationNames {
  getOpenedWindowsCount = 'getOpenedWindowsCount',
  isActiveWindowDialog = 'isActiveWindowDialog',
  isActiveWindowWarning = 'isActiveWindowWarning'
}

export interface WindowManagerOperation extends OptionalOperationProperties {
  mappingName?: string,
  name: WindowManagerOperationNames;
  type: OperationType.windowManager
}

export interface DataOperation extends OptionalOperationProperties {
  type: OperationType.data
}

export interface CancelRequestOperation extends OptionalOperationProperties {
  type: OperationType.cancelRequest
}

export interface CleanUpOperation extends OptionalOperationProperties {
  type: OperationType.cleanUpAction
}

export interface IsModuleMountedOperation extends OptionalOperationProperties {
  type: OperationType.isModuleMounted
}

export type Operation = MaskOperation
  | UnmaskOperation
  | DataOperation
  | PersistentData
  | GenerateOperation
  | DeleteAllOperationDataOperation
  | PingDownloadOperation
  | RequestOperation
  | MergeAsyncRequest
  | StackOperation
  | CookieOperation
  | CloseOperation
  | ConditionOperation
  | ActionOperation
  | SwitchOperation
  | AddOperationDataOperation
  | AddPersistentDataOperation
  | PromiseOperation
  | RemovePersistentDataOperation
  | BaseOperation
  | SyncDispatcherItemsOperation
  | StopAllOperation
  | SignOperation
  | ThemeOperation
  | LanguageOperation
  | UnlinkOperation
  | DialogOperation
  | WarningOperation
  | WidgetOperation
  | RedirectOperation
  | UploadOperation
  | UpdateDialog
  | PopupWindowOperation
  | WindowManagerOperation
  | WebSocketOperation
  | WebSocketFakePingOperation
  | UnmaskAllModuleOperation
  | CancelRequestOperation
  | CleanUpOperation
  | IsModuleMountedOperation

export type Action = Operation[];
